import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';

import { color_gradients_green_shift, color_shades_dark, color_shades_darker, color_shades_darkest, color_variants_shadow_20 } from '../../../../shared/constants/colors';
import { breakpoint_small } from '../../../../shared/constants/breakpoints';
import { css } from '@emotion/css';
import { style_border_default } from '../../../../shared/constants/stylesValues';
import RadioCardGroupWithAudio from '../../../../shared/components/forms/RadioCardGroupWithAudio';
import { SparkTelephonyDetailsFormData, SparkTelephonyDetailsProps } from './SparkTelephonyDetailsForm';
import { FormikProps } from 'formik';
import Button from '../../../../shared/components/general/Button';
import { voiceOptions } from '../../../../shared/constants/sparkConstants/voiceOptions';
import TextField from '../../../../shared/components/forms/TextField';
import TelephonyDeploymentRequest from '../../../../shared/models/telephonyDeployment/TelephonyDeploymentRequest';
import FullScreenLayout from '../../../../shared/scenes/fullScreenLayout';
import PageContainer from '../../../../shared/components/structure/PageContainer';
import ModalLayout from '../../../../shared/components/structure/ModalLayout';
import ModalHeader from '../../../../shared/components/general/ModalHeader';
import FooterBar from "../../../../shared/components/structure/FooterBar";
import CustomScrollbars from '../../../../shared/components/structure/CustomScrollbars';
import useWindowSize from '../../../../shared/hooks/UseWindowSize';
import UserAvatar from '../../../../shared/components/general/UserAvatar';
import MultiFileAudioPlayer from '../../../../shared/components/messagePreviews/MultiFileAudioPlayer';
import SelectField from '../../../../shared/components/forms/SelectField';
import { getLanguageName, getRegionName } from '../../../../shared/models/extensions/languages';
import Switch from '../../../../shared/components/forms/Switch';
import * as voicifyApi from '../../../../shared/api';
import { defaultTelephonyDeploymentSettings } from '../../../../shared/constants/telephonyConstants.tsx/defaultTelephonyDeploymentSettings';
import { spanishTtsVoice, englishMultilingualTtsVoice } from '../../../../shared/constants/sparkConstants/voices';
import { OutboundProvisioningProgressModal } from './OutboundProvisioningProgressModal';
import ConditionalTooltipWrapper from '../../../../shared/components/general/ConditionalTooltipWrapper';

const maleIcon = require('../../../../shared/content/images/spark/male.svg');

const SparkTelephonyDetails: React.FC<SparkTelephonyDetailsProps & FormikProps<SparkTelephonyDetailsFormData>> = (props) => {

    const customVoiceOption = { value: "custom", label: "Custom", icon: maleIcon, description: "Custom voice", audioSrc: "", provider: "", voice: "" };

    const windowSize = useWindowSize();
    const MODAL_SCROLLBAR_SUBTRACTION: number = 200;

    const voiceOptionsWithCustom = [...voiceOptions, customVoiceOption];

    const [showAddVoiceModal, setShowAddVoiceModal] = useState(false);
    const [selectedVoiceModal, setSelectedVoiceModal] = useState(null);
    const [voiceToEdit, setVoiceToEdit] = useState(-1);
    const [selectedLocaleDeployment, setSelectedLocaleDeployment] = useState(props.selectedTelephonyDeployment);
    const [isSaving, setIsSaving] = useState(false);
    const [targetApplicationName, setTargetApplicationName] = useState("");
    const [isMoving, setIsMoving] = useState(false);
    const [showOutboundModal, setShowOutboundModal] = useState(false);

    const handleMovePhoneNumber = async () => {
        if (!props.handleTransferPhoneNumber || !targetApplicationName) {
            return;
        }
        setIsMoving(true);
        const success = await props.handleTransferPhoneNumber(selectedLocaleDeployment.phoneNumber, targetApplicationName);
        if (success) {
            setTargetApplicationName("");
        }
        setIsMoving(false);
    };

    const hasMultipleLocales = props.allTelephonyDeployments && props.allTelephonyDeployments.length > 1;

    // map locale to display name
    const getLocaleDisplayName = (locale: string, deploymentName?: string) => {
        if (!locale) return locale;

        if (locale.length === 2) {
            const languageName = getLanguageName(locale) || locale;
            return deploymentName ? `${languageName} - ${deploymentName}` : languageName;
        } else if (locale.length === 5 && locale.includes('-')) {
            const [languageCode, regionCode] = locale.split('-');
            const languageName = getLanguageName(languageCode) || languageCode;
            const regionName = getRegionName(regionCode) || regionCode;
            const displayName = `${languageName} - ${regionName}`;
            return deploymentName ? `${displayName} - ${deploymentName}` : displayName;
        }

        //Fallback
        return deploymentName ? `${locale} - ${deploymentName}` : locale;
    };

    // This is going track which locale is being edited or right now selected
    const localeOptions = props.allTelephonyDeployments?.map(deployment => ({
        value: deployment.locale,
        label: getLocaleDisplayName(deployment.locale, deployment.name)
    })) || [];

    useEffect(() => {
        if (hasMultipleLocales && props.allTelephonyDeployments) {
            const currentDeployment = props.allTelephonyDeployments.find(
                deployment => deployment.locale === selectedLocaleDeployment.locale
            );
            if (currentDeployment) {
                setSelectedLocaleDeployment(currentDeployment);
                props.setFieldValue("ttsOptions", currentDeployment.ttsOptions || []);
                props.setFieldValue("callRecordingEnabled", currentDeployment.callRecordingEnabled);
                props.setFieldValue("performLanguageDetection", currentDeployment.performLanguageDetection);
                props.setFieldValue("dtmfTurnsToListen", currentDeployment.dtmfTurnsToListen);
                if (currentDeployment.virtualNumber) {
                    props.setFieldValue("virtualNumber", currentDeployment.virtualNumber);
                }
            }
        }
    }, [selectedLocaleDeployment.locale, hasMultipleLocales]);

    const mapTtsOptionsToVoices = (ttsOptions) => {
        if (!ttsOptions || !Array.isArray(ttsOptions)) {
            return [];
        }
        return ttsOptions.map(option => {
            const match = voiceOptions.find(
                fullOption =>
                    fullOption.provider?.toLowerCase() === option.ttsProvider?.toLowerCase() &&
                    fullOption.voice === option.ttsVoice
            );

            if (match) {
                return match;
            } else {
                return {
                    value: "custom",
                    label: "Custom",
                    icon: maleIcon,
                    description: "Custom voice",
                    audioSrc: "",
                    provider: option.ttsProvider,
                    voice: option.ttsVoice
                };
            }
        });
    };
    const [selectedVoices, setSelectedVoices] = useState(mapTtsOptionsToVoices(selectedLocaleDeployment.ttsOptions || []));

    const handleVoiceSelection = (e) => {
        const selectedVoiceRecord = { label: e.label, value: e.value, icon: e.icon, description: e.description, audioSrc: e.audioSrc, provider: e.provider, voice: e.voice }
        setSelectedVoiceModal(selectedVoiceRecord);
    }

    const handleLocaleChange = (selectedOption) => {
        if (props.allTelephonyDeployments) {
            const newDeployment = props.allTelephonyDeployments.find(
                deployment => deployment.locale === selectedOption.value
            );
            if (newDeployment) {
                setSelectedLocaleDeployment(newDeployment);
                setSelectedVoices(mapTtsOptionsToVoices(newDeployment.ttsOptions || []));
            }
        }
    };

    const setEditMode = (index: number) => {
        setVoiceToEdit(index);
        setShowAddVoiceModal(true);
    }

    const handleRemoveVoice = (index: number) => {
        const updated = [...props.values.ttsOptions];
        const updatedFull = [...selectedVoices];
        updated.splice(index, 1);
        updatedFull.splice(index, 1);
        props.setFieldValue("ttsOptions", updated);
        setSelectedVoices(updatedFull);
        setShowAddVoiceModal(false);
        setVoiceToEdit(-1);
    };

    const handleChangeVoice = (index: number) => {
        if (selectedVoiceModal != null) {
            const updatedVoices = [...selectedVoices];
            updatedVoices[index] = selectedVoiceModal;

            const updatedOptions = [...props.values.ttsOptions];
            updatedOptions[index] = { ttsProvider: selectedVoiceModal.provider, ttsVoice: selectedVoiceModal.voice }

            props.setFieldValue("ttsOptions", updatedOptions);
            setSelectedVoices(updatedVoices);
        }
        setSelectedVoiceModal(null);
        setShowAddVoiceModal(false);
        setVoiceToEdit(-1);
    }

    const handleSaveNewVoice = () => {
        if (selectedVoiceModal != null) {
            const updatedFull = [...selectedVoices, selectedVoiceModal];
            const updated = [...props.values.ttsOptions, { ttsProvider: selectedVoiceModal.provider, ttsVoice: selectedVoiceModal.voice }];
            props.setFieldValue("ttsOptions", updated);
            setSelectedVoices(updatedFull)
        }
        setSelectedVoiceModal(null);
        setShowAddVoiceModal(false);
    };

    const handleAddSpanishLine = async () => {
        const enUsDeployment = props.selectedTelephonyDeployment;
        const provisionedPhoneNumber = enUsDeployment.phoneNumber;

        // Step 1: Fetch all languages to get en-US and es-US IDs
        const languagesResult = await voicifyApi.getAllLanguages();
        if (!languagesResult || languagesResult.resultType !== 'Ok' || !languagesResult.data) {
            return;
        }
        const enUsLanguage = languagesResult.data.find(l => l.shortCode === 'en-US');
        const esUsLanguage = languagesResult.data.find(l => l.shortCode === 'es-US');
        if (!enUsLanguage || !esUsLanguage) {
            return;
        }

        // Step 2: Add es-US to the application's languages
        await voicifyApi.updateApplicationLanguages(props.applicationId, [enUsLanguage.id, esUsLanguage.id]);

        // Step 3: Sync NLP and interaction model for es-US using en-US as source
        await voicifyApi.syncNLPAndInteractionModel(props.applicationId, [esUsLanguage.id], enUsLanguage.id);

        // Step 4: Queue NLP deployments to trigger interaction model rebuild
        await voicifyApi.queueDeployments(props.applicationId);

        // Step 5: Add es-US locale config to the same phone number.
        // Spreads the existing en-US deployment so the server receives phoneNumber, phoneNumberCountry,
        // virtualNumber, useVirtualNumber, id, applicationId, etc. as extra fields — matching the
        // manual UI flow in handleMultiLocaleSubmit (TelephonyDeployment.tsx).
        const esUsName = (props.orgName && props.appName)
            ? `${props.orgName} - ${props.appName} - ES`
            : '';

        const esUsRequest: TelephonyDeploymentRequest = {
            ...enUsDeployment,
            phoneNumberStartsWith: provisionedPhoneNumber,
            locale: 'es-US',
            asrModel: 'nova-2',
            performLanguageDetection: false,
            dtmfTurnsToListen: 0,
            name: esUsName,
            ttsOptions: [{ ttsProvider: 'ElevenLabs', ttsVoice: spanishTtsVoice }],
            proactiveResponses: 'Un momento|Veamos|Solo un momento|Espera un segundo|Espera mientras reviso eso|Dame un momento|Espera mientras miro eso',
            followUpProactiveResponses: 'Todavía estoy revisando eso | Sigo investigando esto por ti | Estoy trabajando activamente en esto y te actualizaré pronto | Todavía estoy buscando | Estoy avanzando con esto y te mantendré informado',
            useGenerativeProactiveResponse: true,
            generativeProactiveResponsePrompt: props.assistantType === 'ai-ordering'
                ? '{{constant.generative_proactive_response_ordering_spanish}}'
                : '{{constant.generative_proactive_response_dental_spanish}}',
            maxProactiveResponseAttempts: 3,
        };

        await props.createTelephonyDeployment(props.applicationId, esUsRequest);

        // Also upgrade the en-US deployment to nova-3 to match the wizard flow
        const enUsUpgradeRequest: TelephonyDeploymentRequest = {
            ...enUsDeployment,
            asrModel: 'nova-3',
            performLanguageDetection: false,
            dtmfTurnsToListen: 2,
            ttsOptions: [{ ttsProvider: 'ElevenLabs', ttsVoice: englishMultilingualTtsVoice }],
        };
        await props.handleSubmitTelephonyForm(provisionedPhoneNumber, enUsUpgradeRequest);

        await props.loadTelephonyDeployments();
    };

    const manualSubmit = async () => {
        const isDental = props.assistantType === 'dental-scheduling';
        const isOrdering = props.assistantType === 'ai-ordering';

        const telephonyDeploymentRequest: TelephonyDeploymentRequest = {
            noTracking: selectedLocaleDeployment.noTracking,
            phoneNumberStartsWith: selectedLocaleDeployment.phoneNumber[0] + selectedLocaleDeployment.phoneNumber[1] + selectedLocaleDeployment.phoneNumber[2],
            environmentId: selectedLocaleDeployment.environmentId,
            ttsOptions: props.values.ttsOptions,
            locale: selectedLocaleDeployment.locale,
            asrEndpointing: selectedLocaleDeployment.asrEndpointing,
            asrProvider: selectedLocaleDeployment.asrProvider,
            asrModel: selectedLocaleDeployment.asrModel,
            asrTier: selectedLocaleDeployment.asrTier,
            sonioxContextJson: selectedLocaleDeployment.sonioxContextJson,
            performLanguageDetection: props.values.performLanguageDetection ?? selectedLocaleDeployment.performLanguageDetection,
            dtmfTurnsToListen: props.values.dtmfTurnsToListen ?? selectedLocaleDeployment.dtmfTurnsToListen,
            ttsPronunciationsMap: selectedLocaleDeployment.ttsPronunciationsMap,
            firstMessageDelayMS: selectedLocaleDeployment.firstMessageDelayMS,
            mergeTurnsAfterPauses: selectedLocaleDeployment.mergeTurnsAfterPauses,
            mergeTurnTimeoutMS: selectedLocaleDeployment.mergeTurnTimeoutMS,
            disableBargeIn: selectedLocaleDeployment.disableBargeIn,
            repromptAttempts: selectedLocaleDeployment.repromptAttempts,
            repromptDelaySeconds: selectedLocaleDeployment.repromptDelaySeconds,
            name: selectedLocaleDeployment.name,
            proactiveResponseDelayMS: isDental ? 1900 : selectedLocaleDeployment.proactiveResponseDelayMS,
            proactiveResponses: selectedLocaleDeployment.proactiveResponses,
            useProactiveResponses: selectedLocaleDeployment.useProactiveResponses,
            maxProactiveResponseAttempts: selectedLocaleDeployment.maxProactiveResponseAttempts,
            followUpProactiveResponses: selectedLocaleDeployment.followUpProactiveResponses,
            followUpProactiveResponsesDelayMS: selectedLocaleDeployment.followUpProactiveResponsesDelayMS,
            disableWelcomeMessageBargeIn: selectedLocaleDeployment.disableWelcomeMessageBargeIn,
            disableSpeechStarted: selectedLocaleDeployment.disableSpeechStarted,
            speechStartedUnconfirmedBargeInLimit: selectedLocaleDeployment.speechStartedUnconfirmedBargeInLimit,
            speechStartedPauseMS: selectedLocaleDeployment.speechStartedPauseMS,
            speechStartedRequiredBufferCount: selectedLocaleDeployment.speechStartedRequiredBufferCount,
            callRecordingEnabled: props.values.callRecordingEnabled,
            speechStartedLevel: selectedLocaleDeployment.speechStartedLevel,
            attemptInitialTransfer: selectedLocaleDeployment.attemptInitialTransfer,
            initialTransferTimeoutSeconds: selectedLocaleDeployment.initialTransferTimeoutSeconds,
            initialTransferTarget: selectedLocaleDeployment.initialTransferTarget,
            initialTransferWithCallingNumber: selectedLocaleDeployment.initialTransferWithCallingNumber,
            postProactiveResponsePauseDelayMS: isDental ? 500 : selectedLocaleDeployment.postProactiveResponsePauseDelayMS,
            virtualNumber: props.values.virtualNumber,
            shouldProcessAudio: selectedLocaleDeployment.shouldProcessAudio,
            phoneNumberCountryCode: selectedLocaleDeployment.phoneNumberCountryCode,
            disableSpamDetection: selectedLocaleDeployment.disableSpamDetection,
            immediateTransferHourCutoff: selectedLocaleDeployment.immediateTransferHourCutoff,
            aiReengageSecondsAfterImmediateTransfer: selectedLocaleDeployment.aiReengageSecondsAfterImmediateTransfer,
            spamDetectionPromptOverride: selectedLocaleDeployment.spamDetectionPromptOverride,
            spamDetectionTolerance: selectedLocaleDeployment.spamDetectionTolerance,
            spamDetectionTemperature: selectedLocaleDeployment.spamDetectionTemperature,
            spamDetectionGenAiProvider: selectedLocaleDeployment.spamDetectionGenAiProvider,
            tryWebsocketReconnectionOnWelcome: selectedLocaleDeployment.tryWebsocketReconnectionOnWelcome,
            platform: selectedLocaleDeployment.platform,
            useGenerativeProactiveResponse: selectedLocaleDeployment.useGenerativeProactiveResponse,
            generativeProactiveResponsePrompt: isDental
                ? '{{constant.generative_proactive_response_dental}}'
                : isOrdering
                    ? '{{constant.proactive_response_prompt}}'
                    : selectedLocaleDeployment.generativeProactiveResponsePrompt,
            lastProactiveResponsesToDisallow: selectedLocaleDeployment.lastProactiveResponsesToDisallow,
            notificationEmails: selectedLocaleDeployment.notificationEmails,
            enableBackgroundOverlayAudio: selectedLocaleDeployment.enableBackgroundOverlayAudio ?? defaultTelephonyDeploymentSettings.enableBackgroundOverlayAudio,
            backgroundOverlayAudioUrl: selectedLocaleDeployment.backgroundOverlayAudioUrl ?? defaultTelephonyDeploymentSettings.backgroundOverlayAudioUrl,
            backgroundOverlayAudioLeadInMS: selectedLocaleDeployment.backgroundOverlayAudioLeadInMS ?? defaultTelephonyDeploymentSettings.backgroundOverlayAudioLeadInMS,
        }

        await props.handleSubmitTelephonyForm(selectedLocaleDeployment.phoneNumber, telephonyDeploymentRequest);
    }
    return (
        <div>
            {hasMultipleLocales && (
                <>
                    <RadioGroupLabel>Select Locale</RadioGroupLabel>
                    <Description>Choose the locale configuration to edit. Each locale can have different voice settings.</Description>
                    <LocaleSelectWrapper>
                        <SelectField
                            useForm={false}
                            useFormTheme={true}
                            options={localeOptions}
                            value={selectedLocaleDeployment.locale}
                            onChange={handleLocaleChange}
                            placeholder="Select locale"
                            clearable={false}
                        />
                    </LocaleSelectWrapper>
                    <Divider />
                </>
            )}
            {selectedLocaleDeployment.virtualNumber &&
                <>
                    <RadioGroupLabel>Virtual Number</RadioGroupLabel>
                    <StyledTextField
                        name="virtualNumber"
                        value={props.values.virtualNumber}
                        onChange={(e) => props.setFieldValue("virtualNumber", e.target.value)}
                        placeholder="Update your virtual number here."
                        labelStyle={textFieldLabelStyle}
                    />
                    <Divider />
                </>
            }
            <RadioGroupLabel>Selected Voices</RadioGroupLabel>
            <Description>Here are the currently enabled voices. One will be randomly selected for each incoming call.</Description>
            <StyledVoiceTable>
                {selectedVoices?.map((voice, index) => (
                    <VoiceRow key={index}>
                        <LeftWrapper>
                            <AvatarWrapper>
                                <UserAvatar src={voice.icon} firstName='' lastName='' size={40} />
                            </AvatarWrapper>
                            <NameWrapper>
                                <StyledVoiceName>{voice.value}</StyledVoiceName>
                            </NameWrapper>
                        </LeftWrapper>
                        <RightWrapper>
                            <AudioWrapper>
                                {voice.value != "custom" && (
                                    <AudioPlayerWrapper>
                                        <MultiFileAudioPlayer src={[voice.audioSrc]} isLoading={false} onLoad={() => null} />
                                    </AudioPlayerWrapper>)}
                            </AudioWrapper>
                            <EditButton
                                themes={["secondary-small"]}
                                text="Edit Voice"
                                onClick={() => setEditMode(index)}
                            />
                        </RightWrapper>
                    </VoiceRow>
                ))}
            </StyledVoiceTable>
            <AddANewVoiceButton
                themes={["primary-small"]}
                text="+ Add a New Voice"
                onClick={() => setShowAddVoiceModal(true)}
            />
            <Divider />
            {showAddVoiceModal && (
                <div>
                    <ModalLayout isVisible={true}>
                        <ModalHeader title='Preferred Voice' onClose={() => setShowAddVoiceModal(false)} />
                        <form>
                            <CustomScrollbars autoHide autoHeight autoHeightMax={(windowSize.windowSize.innerHeight * .9) - MODAL_SCROLLBAR_SUBTRACTION}>
                                <VoiceCardsWrapper>
                                    <RadioCardGroupWithAudio
                                        onChange={(e) => handleVoiceSelection(e)}
                                        options={(selectedVoices?.some((option) => option.value === "custom")) ? voiceOptionsWithCustom : voiceOptions}
                                        value={selectedVoiceModal}
                                    />
                                </VoiceCardsWrapper>
                            </CustomScrollbars>
                        </form>
                        <FooterBar>
                            {voiceToEdit >= 0 && (
                                <div>
                                    <Button
                                        disabled={!showAddVoiceModal}
                                        themes={["primary"]}
                                        text="Update Voice"
                                        type="button"
                                        onClick={() => { handleChangeVoice(voiceToEdit); }}
                                    />
                                    {props.values.ttsOptions.length > 1 && selectedVoices.length > 1 && (
                                        <Button
                                            disabled={!showAddVoiceModal}
                                            themes={["secondary"]}
                                            text={`Remove ${selectedVoices[voiceToEdit]["value"]}`}
                                            type="button"
                                            onClick={() => handleRemoveVoice(voiceToEdit)}
                                        />)}
                                </div>
                            )}
                            {voiceToEdit < 0 && (
                                <Button
                                    disabled={!showAddVoiceModal}
                                    themes={["primary"]}
                                    text="Add Voice"
                                    type="button"
                                    onClick={() => { handleSaveNewVoice(); }}
                                />)}
                        </FooterBar>
                    </ModalLayout>
                </div>
            )}
            {!(selectedLocaleDeployment.name?.toLowerCase().includes('outbound') ?? false) && !props.allTelephonyDeployments?.some(d => d.locale === 'es-US') && (
                <>
                    <RadioGroupLabel>Add Spanish Line</RadioGroupLabel>
                    <Description>Automatically creates a Spanish language telephony deployment alongside the English line.</Description>
                    <CustomSwitch
                        name="addSpanishLine"
                        label="Add Spanish Line"
                        detail="Enable to set up an es-US telephony configuration on this phone number using Maria's multilingual voice."
                        checked={props.values.addSpanishLine}
                        onChange={(checked) => props.setFieldValue('addSpanishLine', checked)}
                    />
                    <Divider />
                </>
            )}

            {props.applicationsList && props.applicationsList.length > 0 && (() => {
                const targetApps = props.applicationsList.filter(app => app.id !== props.applicationId);
                const hasTargetApps = targetApps.length > 0;

                return (
                    <>
                        <RadioGroupLabel>Move Phone Number</RadioGroupLabel>
                        <Description>Transfer number from this application to another application</Description>
                        <MovePhoneNumberFieldsWrapper>
                            <SelectField
                                useForm={false}
                                useFormTheme={true}
                                label='Assistant Number'
                                options={[
                                    { 
                                        value: selectedLocaleDeployment.phoneNumber, 
                                        label: selectedLocaleDeployment?.name 
                                            ? `${selectedLocaleDeployment.phoneNumber} - ${selectedLocaleDeployment.name}` 
                                            : selectedLocaleDeployment.phoneNumber 
                                    }
                                ]}
                                onChange={() => null}
                                placeholder='Phone number'
                                value={selectedLocaleDeployment.phoneNumber}
                                clearable={false}
                                className='assistantNumberSelect'
                                disabled={true}
                            />
                            <h3>To</h3>
                            <SelectField
                                useForm={false}
                                useFormTheme={true}
                                label='Target Application'
                                options={
                                    targetApps.map((app) => ({ value: app.name, label: app.name }))
                                }
                                onChange={(e) => setTargetApplicationName(e?.value)}
                                placeholder={hasTargetApps ? 'Select The Target Application' : 'No other applications available'}
                                value={targetApplicationName}
                                clearable={false}
                                className='assistantNumberSelect'
                                disabled={!hasTargetApps}
                            />
                        </MovePhoneNumberFieldsWrapper>
                        <ConditionalTooltipWrapper
                            showTooltip={!hasTargetApps}
                            place="top"
                            text="This option is disabled because there are no other location applications available in this organization to move this phone number to."
                        >
                            <div>
                                <MoveButton
                                    disabled={!hasTargetApps || !targetApplicationName || isMoving}
                                    loading={isMoving}
                                    themes={["primary-small"]}
                                    text='Move Number'
                                    onClick={handleMovePhoneNumber}
                                />
                            </div>
                        </ConditionalTooltipWrapper>
                        <Divider />
                    </>
                );
            })()}

            {selectedLocaleDeployment?.useForOutboundCalling && !selectedLocaleDeployment?.outboundCallingEnabled && (
                <>
                    <RadioGroupLabel>Outbound Calling</RadioGroupLabel>
                    <Description>Enable or configure your automated outbound call campaigns and schedules.</Description>
                    <AddANewVoiceButton
                        themes={['primary-small']}
                        text="Confirm & Go-Live"
                        type="button"
                        onClick={() => setShowOutboundModal(true)}
                    />
                    <Divider />
                    {showOutboundModal && (
                        <OutboundProvisioningProgressModal
                            applicationId={props.applicationId}
                            environmentId={props.selectedTelephonyDeployment?.environmentId || ""}
                            organizationName={props.orgName ?? ''}
                            locationName={props.appName ?? ''}
                            appName={props.appName ?? ''}
                            pmsName=""
                            defaultOfficeId=""
                            timeZone=""
                            medicalIntegrationConfigId=""
                            hasSpanish={props.allTelephonyDeployments?.some(d => d.locale === 'es-US') || false}
                            applicationFeatureFlags={props.applicationFeatureFlags}
                            onClose={() => setShowOutboundModal(false)}
                            onSuccess={() => {
                                setShowOutboundModal(false);
                                props.loadTelephonyDeployments();
                            }}
                            orgId={props.orgId}
                        />
                    )}
                </>
            )}

            <SaveButtonWrapper>
                <Button
                    disabled={!selectedLocaleDeployment || isSaving}
                    loading={isSaving || props.isLoadingTelephonyDeployments}
                    themes={["primary"]}
                    text="Save Phone Config"
                    onClick={async () => {
                        setIsSaving(true);

                        if (props.values.addSpanishLine) {
                            await handleAddSpanishLine();
                            props.onMultilingualEnabled?.();
                            props.setFieldValue('addSpanishLine', false);
                        } else {
                            props.handleSubmit();
                            await manualSubmit();
                        }

                        setIsSaving(false);
                    }}
                />
            </SaveButtonWrapper>
        </div>
    );
};

const AddANewVoiceButton = styled(Button)`
    margin-left: 0;
    margin-right: 0;
`;

const RightWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

const LeftWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const AvatarWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

const AudioPlayerWrapper = styled.div`
    flex: 1;
    margin-top: 10px;
`;

const StyledVoiceTable = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 16px;
`;

const VoiceRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border: ${style_border_default};
    border-top: none;
    border-radius: 8px;
    height: 60px;
    &:first-of-type {
        border-top: ${style_border_default};
    }
`;

const NameWrapper = styled.div`
`;

const AudioWrapper = styled.div`
    width: 500px;
    ${breakpoint_small} {
        display: none;
    }
`;

const StyledVoiceName = styled.span`
    font-size: 16px;
    font-weight: 500;
    color: ${color_shades_darkest};
`;

const EditButton = styled(Button)``;

const textFieldLabelStyle = css`
    margin: 16px 0 0 0;
`;

const StyledTextField = styled(TextField)``;

const Description = styled.p`
    font-size: 14px;
    color: ${color_shades_darker};
    margin-top: 4px;
`;

const VoiceCardsWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;  
`;

const RadioGroupLabel = styled.p`
    font-weight: 400;
    font-size: 18px;
    color: ${color_shades_darkest};
    margin-top: 24px;
    &:first-of-type {
        margin-top: 0;
    }
`;

const LocaleSelectWrapper = styled.div`
    margin-bottom: 24px;
    margin-top: -16px;
    max-width: 600px;
`;

const CustomSwitch = styled(Switch)`
    margin-top: 8px;
    margin-bottom: 8px;
`;

const ScheduleButton = styled(Button)`
    margin-left: 0;
    margin-right: 0;
`;

const MoveButton = styled(Button)`
    margin-left: 0;
    margin-right: 0;
`;

const Divider = styled.div`
    border-bottom: 1px solid ${color_shades_dark};
    margin: 24px 0;
`;

const MovePhoneNumberFieldsWrapper = styled.div`
    width: 100%;
    align-items: center;
    display: flex;
    margin-top: 20px;
    h3 {
        margin: 0 20px;
    } 
    .assistantNumberSelect {
        width: 100%;
    }
`;

const InfoMessage = styled.p`
    font-size: 13px;
    color: #e65100;
    margin-top: 12px;
    font-weight: 500;
`;

const SaveButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
`;

export default SparkTelephonyDetails;

